// DEV-ONLY feature-flag overrides.
//
// Why this exists: flag values come from `GET /config/flags`, which resolves
// against the database. A local API has no DB wired (`sa_session_factory_not_wired`),
// so every flag reports its registry default and any flag-gated surface is
// unreachable locally — you cannot walk the flow you are building.
//
// PRODUCTION SAFETY. Every entry point is guarded by `import.meta.env.DEV`,
// which Vite replaces with the literal `false` at build time. The guarded
// branches are then dead code and are dropped by the minifier, so a production
// bundle contains no way to reach any of this — not via localStorage, not via
// a query parameter, not via the console. `devFlagOverridesEnabled()` is the
// single gate; nothing here should be called without consulting it.
//
// Usage:
//   ?flags=my_flag                 turn one or more on (comma-separated)
//   ?flags=my_flag:false           set an explicit value
//   ?flags=-my_flag                remove one override
//   ?flags=clear                   remove all overrides
// Overrides persist in localStorage so they survive a reload and a redirect
// out to another route, which is the whole point for multi-page flows.

import { isApiRecord, type ApiValue } from '../api/responseDecoders'

export type FlagOverrideValue = boolean | number | string

const STORAGE_KEY = 'dw.dev-flag-overrides.v1'
export const DEV_FLAGS_PARAM = 'flags'

/** The one gate. Statically false — and therefore tree-shaken — in prod. */
export function devFlagOverridesEnabled(): boolean {
  return import.meta.env.DEV
}

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage
  } catch {
    return null
  }
}

/** Coerce a `key:value` fragment. Bare `key` means `true`. */
function parseValue(raw: string | undefined): FlagOverrideValue {
  if (raw === undefined || raw === '') return true
  if (raw === 'true') return true
  if (raw === 'false') return false
  const asNumber = Number(raw)
  if (raw.trim() !== '' && Number.isFinite(asNumber)) return asNumber
  return raw
}

/** Current overrides. Always `{}` outside dev, or if storage is unreadable. */
export function readDevFlagOverrides(): Record<string, FlagOverrideValue> {
  if (!devFlagOverridesEnabled()) return {}
  const store = storage()
  if (store === null) return {}
  try {
    const raw = store.getItem(STORAGE_KEY)
    if (raw === null) return {}
    const parsed: ApiValue = JSON.parse(raw)
    if (!isApiRecord(parsed)) return {}
    const out: Record<string, FlagOverrideValue> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
        out[key] = value
      }
    }
    return out
  } catch {
    return {}
  }
}

function write(overrides: Record<string, FlagOverrideValue>): void {
  const store = storage()
  if (store === null) return
  try {
    if (Object.keys(overrides).length === 0) store.removeItem(STORAGE_KEY)
    else store.setItem(STORAGE_KEY, JSON.stringify(overrides))
  } catch {
    /* a dev convenience is not worth throwing over */
  }
}

/**
 * Apply `?flags=…` to the stored overrides.
 *
 * Returns true when something changed, so the caller can re-render. The param
 * itself is left on the URL for the caller to strip — this function does not
 * own the query string.
 */
export function applyDevFlagOverridesFromUrl(search: string): boolean {
  if (!devFlagOverridesEnabled()) return false
  const spec = new URLSearchParams(search).get(DEV_FLAGS_PARAM)
  if (spec === null) return false

  const before = readDevFlagOverrides()
  const next = { ...before }

  for (const entry of spec.split(',').map((s) => s.trim()).filter(Boolean)) {
    if (entry === 'clear') {
      for (const key of Object.keys(next)) delete next[key]
      continue
    }
    if (entry.startsWith('-')) {
      delete next[entry.slice(1)]
      continue
    }
    const [key, rawValue] = entry.split(':')
    if (key) next[key] = parseValue(rawValue)
  }

  const changed = JSON.stringify(before) !== JSON.stringify(next)
  if (changed) write(next)
  return changed
}

/**
 * Merge overrides over a resolved snapshot. Returns `flags` unchanged in prod.
 *
 * `overrides` is a PARAMETER rather than being re-read from storage inside, so
 * this is pure: the caller's memo can depend on it honestly instead of hiding
 * a storage read behind a dependency the linter cannot see.
 */
export function withDevFlagOverrides<T extends Record<string, FlagOverrideValue>>(
  flags: T,
  overrides: Record<string, FlagOverrideValue>,
): T | Record<string, FlagOverrideValue> {
  if (!devFlagOverridesEnabled()) return flags
  if (Object.keys(overrides).length === 0) return flags
  return { ...flags, ...overrides }
}
